import {
  LocalizationProvider,
  LocalizationProviderProps,
} from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { Moment } from 'moment';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectedLanguageContext } from '~/components/SelectedLanguage/context';
import { useSyncedLocalStorage } from '~/hooks/useLocalStorage';
import {
  LANGUAGE_KEY,
  getAdapterLocale,
  getMomentDateLibInstance,
} from '~/localization';

type PropsType = {
  children?: ReactNode;
};

export const LanguageAndLocalizationProvider = (props: PropsType) => {
  const { children } = props;

  const { i18n } = useTranslation();
  const updateTranslationsLanguage = useCallback(
    (language: string | undefined) => {
      if (!!language && i18n.language !== language) {
        i18n.changeLanguage(language);
      }
    },
    [i18n],
  );

  const { value: language, setValue } = useSyncedLocalStorage<
    string | undefined
  >(LANGUAGE_KEY, 'en');
  const setLanguage = useCallback(
    (language: string | undefined) => {
      setValue(language);
      updateTranslationsLanguage(language);
    },
    [setValue, updateTranslationsLanguage],
  );

  const localizationProps: LocalizationProviderProps<Moment, string> =
    useMemo(() => {
      return {
        adapterLocale: getAdapterLocale(language),
        dateAdapter: AdapterMoment,
        dateLibInstance: getMomentDateLibInstance(language),
      };
    }, [language]);

  return (
    <SelectedLanguageContext.Provider
      value={{
        language,
        setLanguage,
      }}
    >
      <LocalizationProvider {...localizationProps}>
        {children}
      </LocalizationProvider>
    </SelectedLanguageContext.Provider>
  );
};
